import React, { useEffect, useState } from "react";
import { message, Empty } from "antd";
import GenericStatCard from "../../../Components/Cards/GenericStatCard";
import StackedBarChart from "../../../Components/Charts/StackedBarChart";
import LineChart from "../../../Components/Charts/LineChart";
import Barchart from "../../../Components/Charts/BarChart";
import fuelIcon from "../../../Assets/Icons/fuel-icon.svg";
import fleetIcon from "../../../Assets/Icons/laptop_car.svg";
import activeVehicleIcon from "../../../Assets/Icons/delivery_truck_bolt.svg";
import vendorIcon from "../../../Assets/Icons/storefront.svg";
import orderIcon from "../../../Assets/Icons/order_approve.svg";
import outstandingBalanaceIcon from "../../../Assets/Icons/outstandings_balance.svg";
import creditIcon from "../../../Assets/Icons/credit_card_clock.svg";
import GenericTable from "../../../Components/Tables/GenericTable";
import { getAdminDashboard } from "../../../API/Admin/Dashboard";
import "./style.css";
import { getInitials } from "../../../Components/InitialsAvatar";

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const formatCurrency = (value) =>
  `$${(parseFloat(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const ImageAvatar = ({ src, name }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: "5px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          color: "#fff",
          background: getAvatarColor(name),
          flexShrink: 0,
        }}
      >
        {getInitials(name)}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      style={{
        width: 24,
        height: 24,
        borderRadius: "5px",
        objectFit: "cover",
        flexShrink: 0,
      }}
      onError={() => setFailed(true)}
    />
  );
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("Volume");
  const [selectedRows, setSelectedRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [creditUtilization, setCreditUtilization] = useState(null);
  const [fuelSpendTrend, setFuelSpendTrend] = useState({
    labels: [],
    spend: [],
    volume: [],
  });
  const [topFleets, setTopFleets] = useState({
    labels: [],
    total_volume: [],
    total_amount: [],
  });
  const [spendByFuelType, setSpendByFuelType] = useState({
    labels: [],
    spend: [],
  });
  const [topVendorsByTransactions, setTopVendorsByTransactions] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await getAdminDashboard();

        if (Math.floor(res?.status / 100) === 2) {
          const data = res?.data?.data;

          setStats(data?.stats || null);
          setCreditUtilization(data?.credit_utilization || null);
          setFuelSpendTrend({
            labels: data?.fuel_spend_trend?.labels || [],
            spend: (data?.fuel_spend_trend?.spend || []).map((v) =>
              parseFloat(v) || 0,
            ),
            volume: (data?.fuel_spend_trend?.volume || []).map(
              (v) => parseFloat(v) || 0,
            ),
          });
          setTopFleets({
            labels: data?.top_fleets?.labels || [],
            total_volume: (data?.top_fleets?.total_volume || []).map(
              (v) => parseFloat(v) || 0,
            ),
            total_amount: (data?.top_fleets?.total_amount || []).map(
              (v) => parseFloat(v) || 0,
            ),
          });
          setSpendByFuelType({
            labels: data?.spend_by_fuel_type?.labels || [],
            spend: (data?.spend_by_fuel_type?.total_amount || []).map(
              (v) => parseFloat(v) || 0,
            ),
          });
          setTopVendorsByTransactions(
            Array.isArray(data?.top_vendors_by_transactions)
              ? data.top_vendors_by_transactions
              : [],
          );
          setRecentTransactions(data?.recent_transactions || []);
        } else {
          message.error(
            getErrorMessage(res?.data, "Failed to load dashboard data."),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load dashboard data."),
        );
      }
    };

    fetchDashboard();
  }, []);

  const used = parseFloat(creditUtilization?.used) || 0;
  const limit = parseFloat(creditUtilization?.limit) || 0;
  const available = parseFloat(creditUtilization?.available) || limit - used;
  const percentageUsed = creditUtilization?.percentage_used ?? 0;

  const fleetSpendRanking = topFleets.labels.map((label, index) => ({
    key: label,
    label,
    amount: topFleets.total_amount[index] || 0,
  }));
  const maxFleetSpend = Math.max(1, ...fleetSpendRanking.map((f) => f.amount));

  const vendorRanking = topVendorsByTransactions.map((item, index) => ({
    key: item.fuel_vendor_id ?? index,
    label: item.fuel_vendor_name || "-",
    value:
      activeTab === "Volume"
        ? parseFloat(item.total_volume) || 0
        : parseFloat(item.total_amount) || 0,
  }));
  const maxVendorValue = Math.max(1, ...vendorRanking.map((v) => v.value));

  const dataSource = recentTransactions.map((item, index) => ({
    key: item.id ?? index,
    date: `${item.date || "-"} ${item.time || ""}`.trim(),
    txnId: item.txn_id || "-",
    fleetName: item.fleet_company_name || "-",
    fleetAvatar: resolveAvatarUrl(item.fleet_company_avatar),
    vehicleId: item.vehicle_id || "-",
    vendor: item.fuel_vendor_name || "-",
    vendorAvatar: resolveAvatarUrl(item.fuel_vendor_avatar),
    fuelType: item.fuel_type_name || "-",
    amount: formatCurrency(item.amount),
    status: capitalize(item.status) || "-",
  }));

  const getUniqueFilters = (data, key) =>
    [...new Set(data.map((item) => item[key]))].map((value) => ({
      text: value,
      value,
    }));

  const fuelTypeFilters = getUniqueFilters(dataSource, "fuelType");
  const statusFilters = getUniqueFilters(dataSource, "status");

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
    },
    {
      title: "Txn ID",
      dataIndex: "txnId",
      key: "txnId",
      enableSearch: true,
    },

    {
      title: "Fleet Name",
      dataIndex: "fleetName",
      key: "fleetName",
      enableSearch: "true",
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ImageAvatar src={record.fleetAvatar} name={text} />
          <span style={{ color: "#4F46E5" }}>{text}</span>
        </span>
      ),
    },
    {
      title: "Vehicle ID",
      dataIndex: "vehicleId",
      key: "vehicleId",
      enableSearch: true,
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      enableSearch: "true",
      render: (text, record) => (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <ImageAvatar src={record.vendorAvatar} name={text} />
          {text}
        </span>
      ),
    },
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",
      filters: fuelTypeFilters,
      onFilter: (value, record) => record.fuelType === value,
      render: (text) => <span className="fuel-type-badge">{text}</span>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) =>
        parseFloat(a.amount.replace(/[^0-9.-]+/g, "")) -
        parseFloat(b.amount.replace(/[^0-9.-]+/g, "")),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={`status-badge ${text === "Approved" ? "approved" : "denied"}`}
        >
          {text}
        </span>
      ),
    },
  ];

  return (
    <div className="admin-dashboard">
      <div className="row">
        <div className="tabs">
          <GenericStatCard
            title="Total Fuel Spend"
            value={formatCurrency(stats?.total_fuel_spend?.value)}
            {...(stats?.total_fuel_spend?.percentage != null && {
              percentage: `${stats.total_fuel_spend.percentage}`,
            })}
            isIncrease={stats?.total_fuel_spend?.is_increase}
            subtitle="vs. last month"
            icon={fuelIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Active Fleet"
            value={stats?.total_active_fleets?.value ?? 0}
            {...(stats?.total_active_fleets?.percentage != null && {
              percentage: `${stats.total_active_fleets.percentage}`,
            })}
            isIncrease={stats?.total_active_fleets?.is_increase}
            subtitle="vs. last month"
            icon={fleetIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Active Vehicles"
            value={stats?.total_active_vehicles?.value ?? 0}
            {...(stats?.total_active_vehicles?.percentage != null && {
              percentage: `${stats.total_active_vehicles.percentage}`,
            })}
            isIncrease={stats?.total_active_vehicles?.is_increase}
            subtitle="vs. last month"
            icon={activeVehicleIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Vendors"
            value={stats?.total_vendors?.value ?? 0}
            {...(stats?.total_vendors?.percentage != null && {
              percentage: `${stats.total_vendors.percentage}`,
            })}
            isIncrease={stats?.total_vendors?.is_increase}
            subtitle="vs. last month"
            icon={vendorIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Fuel Transactions"
            value={stats?.fuel_transactions?.value ?? 0}
            {...(stats?.fuel_transactions?.percentage != null && {
              percentage: `${stats.fuel_transactions.percentage}`,
            })}
            isIncrease={stats?.fuel_transactions?.is_increase}
            subtitle="vs. last month"
            icon={orderIcon}
          />
        </div>
        <div className="tabs-credit">
          <GenericStatCard
            title="Credit Utilization"
            value={`${percentageUsed}%`}
            isIncrease={false}
            subtitle="of total limit"
            icon={creditIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Vendor Outstanding Balance"
            value={formatCurrency(stats?.vendor_outstanding_balance?.value)}
            {...(stats?.vendor_outstanding_balance?.percentage != null && {
              percentage: `${stats.vendor_outstanding_balance.percentage}`,
            })}
            isIncrease={stats?.vendor_outstanding_balance?.is_increase}
            subtitle="vs. last month"
            icon={outstandingBalanaceIcon}
          />
        </div>
        <div className="tabs card">
          <div className="credit-card">
            <h3 className="credit-title">Credit Utilization in JMD</h3>

            <div className="credit-bar-section">
              <span className="label">Available Credits</span>
              <span className="available-amount">
                ${available.toLocaleString()}
              </span>
            </div>

            <div className="credit-bar">
              <div
                className="credit-bar-fill"
                style={{ width: `${percentageUsed}%` }}
              ></div>
            </div>

            <div className="credit-footer">
              <span>
                Used:<span> ${used.toLocaleString()}</span>
              </span>
              <span>
                Limit: <span>${limit.toLocaleString()}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="graph">
          <LineChart
            graphData={{
              labels: fuelSpendTrend.labels,
              Spend: fuelSpendTrend.spend,
              Volume: fuelSpendTrend.volume,
            }}
            lengthKeys={2}
            colourTheme={["#F59E0B", "#4F46E5"]}
            graphTitle="Fuel Spend"
            selectTenByDefault={false}
            units={["$", "L"]}
            classNameCSS="fuel-spend"
          />
        </div>
        <div className="graph">
          <StackedBarChart
            graphData={{
              labels: topFleets.labels,
              total_volume: topFleets.total_volume,
              total_amount: topFleets.total_amount,
            }}
            lengthKeys={2}
            colourTheme={["#4F46E5", "#EA580C"]}
            graphTitle="Top Fleets"
            selectTenByDefault={false}
            units={["L", "$"]}
            classNameCSS="top-fleets"
          />
        </div>
      </div>
      <div className="row">
        <div className="dashboard-card">
          <div className="card-header">
            <div className="card-title">Top Fleet by Spend</div>
          </div>

          {fleetSpendRanking.length > 0 ? (
            fleetSpendRanking.map((fleet) => (
              <div className="stat-item" key={fleet.key}>
                <div className="stat-label">{fleet.label}</div>
                <div className="progress-container">
                  <div className="progress-bar">
                    <div
                      className="progress-fill progress-green"
                      style={{
                        width: `${(fleet.amount / maxFleetSpend) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <div className="stat-value">{formatCurrency(fleet.amount)}</div>
                </div>
              </div>
            ))
          ) : (
            <Empty description="No Top Fleet by Spend data available" />
          )}
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <div className="card-title">Top Fuel Stations by Transactions</div>

            <div className="toggle-group">
              {["Volume", "Sale"].map((tab) => (
                <button
                  key={tab}
                  className={`toggle-btn ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {vendorRanking.length > 0 ? (
            vendorRanking.map((vendor) => (
              <div className="stat-item" key={vendor.key}>
                <div className="stat-label">{vendor.label}</div>
                <div className="progress-container">
                  <div className="progress-bar">
                    <div
                      className="progress-fill progress-purple"
                      style={{
                        width: `${(vendor.value / maxVendorValue) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <div className="stat-value">
                    {activeTab === "Volume"
                      ? `${vendor.value.toLocaleString()} L`
                      : formatCurrency(vendor.value)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <Empty description="No Top Fuel Stations by Transactions data available" />
          )}
        </div>
      </div>
      <div className="row">
        <div className="graph">
          <div className="graph fueldata-graph-dashboard">
            <Barchart
              graphData={{
                labels: spendByFuelType.labels,
                spend: spendByFuelType.spend,
              }}
              lengthKeys={1}
              colourTheme={["#EA580C"]}
              graphTitle="Spend By Fuel Type"
              selectTenByDefault={false}
              units={["$"]}
              classNameCSS="quantity-to-use"
              grayzoned={true}
            />
          </div>
        </div>
      </div>

      <div className="fuel-table">
        <GenericTable
          title="Fuel Transactions"
          dataSource={dataSource}
          columns={columns}
          onSelectionChange={(keys, rows) => setSelectedRows(rows)}
          onRowClick={(record) => console.log("Row clicked:", record)}
        />
      </div>
    </div>
  );
};

export default Dashboard;
